import { useCallback, useState } from 'react';
import { fetchCampaignFormTemplates } from '../services/onboardingService';

function newFieldId() {
  return crypto.randomUUID?.() ?? String(Date.now() + Math.random());
}

function mapTemplateFields(fields) {
  return fields.map((f) => ({
    id: f.id || newFieldId(),
    alias_name: f.label ?? f.alias_name ?? '',
    key_name: f.key_name ?? f.label ?? '',
    key_type: f.type ?? f.key_type ?? 'text',
    is_required: Boolean(f.required ?? f.is_required),
    label: f.label,
    type: f.type,
    required: f.required,
  }));
}

export function useCampaignTemplates() {
  const [loading, setLoading] = useState(false);
  const [missingGoals, setMissingGoals] = useState([]);
  const [error, setError] = useState(null);

  const buildCampaignRows = useCallback(async (businessTypeId, selectedGoals, existing) => {
    if (!businessTypeId || selectedGoals.length === 0) {
      return [];
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetchCampaignFormTemplates(businessTypeId, selectedGoals);
      if (!res.ok || !res.data?.templates) {
        setError('Could not load campaign templates.');
        setMissingGoals([]);
        return selectedGoals.map((goal) => {
          const prev = existing.find((c) => c.goal === goal);
          return (
            prev ?? {
              goal,
              name: '',
              form_fields: [],
            }
          );
        });
      }
      setMissingGoals(res.data.missing_goals ?? []);
      const byGoal = Object.fromEntries(res.data.templates.map((t) => [t.goal, t]));
      return selectedGoals.map((goal) => {
        const prev = existing.find((c) => c.goal === goal);
        const tpl = byGoal[goal];
        if (!tpl) {
          return (
            prev ?? {
              goal,
              name: '',
              form_fields: [],
            }
          );
        }
        const sameTemplate = prev?.template_id != null && prev.template_id === tpl.template_id;
        const keepName = sameTemplate && (prev?.name?.trim() ?? '').length > 0;
        const keepFields = sameTemplate && (prev?.form_fields?.length ?? 0) > 0;
        return {
          goal,
          name: keepName ? prev.name : tpl.campaign_name,
          template_id: tpl.template_id,
          form_fields: keepFields ? prev.form_fields : mapTemplateFields(tpl.form_fields),
          proposal_id: prev?.proposal_id ?? null,
          entity_form_id: prev?.entity_form_id ?? null,
          workflow: prev?.workflow ?? {},
          tentative_cost: prev?.tentative_cost ?? null,
          cost_of_lead: prev?.cost_of_lead ?? null,
          brand: prev?.brand ?? 'single_brand',
          description: prev?.description ?? '',
          principal_vendor: prev?.principal_vendor,
          account_id: prev?.account_id,
          organisation_id: prev?.organisation_id,
          vendorData: prev?.vendorData ?? [1, 2, 3],
          centers: prev?.centers ?? [],
        };
      });
    } finally {
      setLoading(false);
    }
  }, []);

  return { loading, error, missingGoals, buildCampaignRows };
}
